/**
 * The gear menu, in the top-right corner: the camera views and the way back
 * to the opening one, full screen, help and settings.
 *
 * It is a real menu — roles, checked states, Escape and click-away — because
 * it is the one control on the deck that is a list rather than a thing.
 */

import { type Component, For, onSettled, Show } from 'solid-js';
import { applyView, resetView } from '../../app/deck-commands.ts';
import { toggleFullscreen } from '../../app/fullscreen.ts';
import { fullscreen, menuOpen, setMenuOpen, setModal, view } from '../../state/ui.ts';
import type { ViewName } from '../../three/views.ts';
import {
  AngleView,
  ArmView,
  Check,
  FrontView,
  Fullscreen,
  FullscreenExit,
  Gear,
  Help,
  ResetView,
  TopView,
} from '../icons/nav.tsx';
import glass from '../primitives/Glass.module.css';
import { GlassButton } from '../primitives/Glass.tsx';
import styles from './Menu.module.css';

interface ViewOption {
  name: ViewName;
  label: string;
  key: string;
  icon: Component;
}

const VIEW_OPTIONS: readonly ViewOption[] = [
  { name: 'top', label: 'Record and arm', key: '1', icon: TopView },
  { name: 'angle', label: 'Angled', key: '2', icon: AngleView },
  { name: 'front', label: 'Front', key: '3', icon: FrontView },
  { name: 'arm', label: 'Tonearm close-up', key: '4', icon: ArmView },
];

const close = (): void => {
  setMenuOpen(false);
};

/** Runs an action, then shuts the menu behind it. */
const then = (action: () => void) => (): void => {
  action();
  close();
};

export const Menu: Component = () => {
  let root!: HTMLDivElement;

  /* Click-away: a press anywhere outside an open menu closes it. One listener
     for the component's life — `onSettled` runs its callback untracked, so
     it cannot be re-run by the open state; the state is read at press time. */
  onSettled(() => {
    const onDown = (e: PointerEvent): void => {
      if (menuOpen() && !root.contains(e.target as Node)) close();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
    };
  });

  return (
    <div class={styles.root} ref={root} data-testid="menu-root">
      <GlassButton
        label="Menu"
        title="View, full screen and help"
        popup="menu"
        expanded={menuOpen()}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <Gear />
      </GlassButton>

      <div
        class={[glass.glass, styles.menu, { [styles.shown as string]: menuOpen() }]}
        role="menu"
        aria-label="Menu"
        data-testid="menu"
      >
        <div class={styles.section}>View</div>
        <For each={VIEW_OPTIONS}>
          {(option) => (
            <button
              type="button"
              role="menuitemradio"
              class={styles.item}
              data-view={option.name}
              aria-checked={view() === option.name ? 'true' : 'false'}
              onClick={then(() => applyView(option.name))}
            >
              <span class={styles.icon}>
                <option.icon />
              </span>
              <span class={styles.label}>{option.label}</span>
              <kbd>{option.key}</kbd>
              <span class={styles.check}>
                <Show when={view() === option.name}>
                  <Check />
                </Show>
              </span>
            </button>
          )}
        </For>
        <button
          type="button"
          role="menuitem"
          class={styles.item}
          data-testid="menu-reset"
          onClick={then(resetView)}
        >
          <span class={styles.icon}>
            <ResetView />
          </span>
          <span class={styles.label}>Reset view</span>
          <kbd>0</kbd>
        </button>

        <div class={styles.rule} />

        <button
          type="button"
          role="menuitemcheckbox"
          class={styles.item}
          data-testid="menu-fullscreen"
          aria-checked={fullscreen() ? 'true' : 'false'}
          onClick={then(toggleFullscreen)}
        >
          <span class={styles.icon}>
            <Show when={fullscreen()} fallback={<Fullscreen />}>
              <FullscreenExit />
            </Show>
          </span>
          <span class={styles.label}>{fullscreen() ? 'Leave full screen' : 'Full screen'}</span>
          <kbd>F</kbd>
        </button>

        <div class={styles.rule} />

        <button
          type="button"
          role="menuitem"
          class={styles.item}
          data-testid="menu-help"
          onClick={then(() => setModal('help'))}
        >
          <span class={styles.icon}>
            <Help />
          </span>
          <span class={styles.label}>Help and shortcuts</span>
          <kbd>?</kbd>
        </button>
        <button
          type="button"
          role="menuitem"
          class={styles.item}
          data-testid="menu-settings"
          onClick={then(() => setModal('settings'))}
        >
          <span class={styles.icon}>
            <Gear />
          </span>
          <span class={styles.label}>Settings</span>
        </button>
      </div>
    </div>
  );
};
